import { canPmUpdate } from '../../../utils/permissions';
import InputComponent from '../../../components/common/InputComponent/InputComponent';
import { Form, Radio, Upload } from 'antd';
import RenderModal from '../../../components/common/Modal/Modal';
import { Icon } from '../../../components/icon/icon';
import ButtonSecondarySmall from '../../../components/common/ButtonSecondary/ButtonSecondarySmall';
import ButtonPrimaryLarge from '../../../components/common/ButtonPrimary/ButtonPrimaryLarge';
import { normFileWithValidation } from '../../../utils/file/file.utils';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  updateSaleProduct,
  fetchSpecialProducts,
} from '../../../store/actions/sale-product.action';
import { openMessageSuccess } from '../../../components/common/Message/openMessageSuccess';
import { PlusOutlined } from '@ant-design/icons';
import ButtonIconGhostSmall from '../../../components/common/ButtonIconGhost/ButtonIconGhostSmall';
import { convertForeignCurrency } from '../../../utils/currency/currency.utils';

const ModalUpdateSpecialProduct = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const fileInputRef2 = useRef(null);
  const thumbnailWatch = Form.useWatch('thumbnail', form);
  const imagesWatch = Form.useWatch('images', form);
  const priceWithInstallWatch = Form.useWatch('price_with_install', form);
  const [searchParams] = useSearchParams();
  const customerEmailParam = searchParams.get('email') ?? '';
  const { updateSaleProduct: updateSaleProductLoading } = useSelector(
    state => state.saleProduct
  );
  const { user } = useSelector(state => state.auth);

  const submit = values => {
    const formData = new FormData();
    formData.append('design_for', values.design_for);
    formData.append('is_outside', true);
    formData.append('name', values.name);
    formData.append('size', JSON.stringify(values.sizes));
    formData.append('price_with_install', values.price_with_install ?? 0);
    formData.append('description', values.description ?? '');
    formData.append('outside_status', 0);
    formData.append('sale_type', 'normal');

    if (values.thumbnail && values.thumbnail.length > 0) {
      formData.append('thumbnail', values.thumbnail[0].originFileObj);
    }
    if (values.images && values.images.length > 0) {
      values.images.forEach(image => {
        if (!image.originFileObj) {
          formData.append(`image_urls`, image.url);
        } else {
          formData.append(`images`, image.originFileObj);
        }
      });
    }
    dispatch(updateSaleProduct({ id: open?.id, data: formData })).then(
      action => {
        if (action.payload?.data) {
          openMessageSuccess({ content: t('Product updated successfully') });
        }
      }
    );
  };

  useEffect(() => {
    if (open) {
      const sizes = open.sizes || {};
      form.setFieldsValue({
        ...open,
        sizes: {
          length: sizes.length,
          depth: sizes.depth,
          height: sizes.height,
        },
        price_with_install: open.price_original,
        thumbnail: [
          {
            uid: '-1',
            name: open.thumbnail?.split('/').slice(-1)[0] ?? 'thumbnail.jpg',
            status: 'done',
            originFileObj: null,
            url: open.thumbnail_url,
          },
        ],
        images: [
          ...(open.image_urls ?? []).map((url, index) => ({
            uid: `-${index + 1}`,
            name: url.split('/').slice(-1)[0] ?? `image${index + 1}.jpg`,
            status: 'done',
            originFileObj: null,
            url,
          })),
        ],
      });
    }
  }, [open]);

  if (open) {
    return (
      <RenderModal
        title={
          <span className="text-headline-small">
            {t('Update special product')}
          </span>
        }
        open={!!open}
        onClose={onClose}
        classNames={{
          header:
            '!p-24 !border-b-[1px] !border-solid !border-outline-default-primary !mb-0',
          content: '!rounded-[16px]',
          body: '!p-0',
          footer:
            '!p-24 !border-t-[1px] !border-solid !border-outline-default-primary !mt-0',
        }}
        width={800}
        footer={[
          <ButtonPrimaryLarge
            title={t('Save changes')}
            loading={updateSaleProductLoading.isLoading}
            disabled={updateSaleProductLoading.isLoading}
            onClick={() => {
              form
                .validateFields()
                .then(values => {
                  submit(values);
                })
                .catch(error => {
                  console.log('Validate Failed:', error);
                });
            }}
          />,
        ]}
        content={
          <Form
            form={form}
            layout="vertical"
            className="flex items-start px-24 py-16 gap-32 grow self-stretch h-[520px] overflow-y-auto"
          >
            <div className="flex w-[140px] flex-col items-center gap-8">
              <div className="flex h-[116px] flex-col items-center justify-center self-stretch rounded-[4px] border-[1px] border-solid border-outline-default-primary">
                <img
                  src={thumbnailWatch?.[0]?.url}
                  alt=""
                  className="object-cover w-full"
                />
              </div>
              <Form.Item
                name={'thumbnail'}
                valuePropName="fileList"
                getValueFromEvent={e => normFileWithValidation({ e })}
              >
                <div>
                  <ButtonSecondarySmall
                    posIcon={<Icon.ICUpload />}
                    title={t('Upload Image')}
                    type={'button'}
                    onClick={() => {
                      fileInputRef.current.click();
                    }}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept="image/*"
                  />
                </div>
              </Form.Item>
            </div>
            <div className="flex flex-col justify-center items-start gap-16 grow">
              <Form.Item
                label={t('Customer email')}
                name="design_for"
                className="self-stretch"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value)
                        return Promise.reject(t('Please enter customer email'));
                      else return Promise.resolve();
                    },
                  },
                  {
                    type: 'email',
                    message: t('Please enter a valid email format'),
                  },
                ]}
                initialValue={customerEmailParam}
              >
                <InputComponent
                  size="large"
                  placeholder={t('Enter customer email')}
                />
              </Form.Item>
              <Form.Item
                label={t('Product name')}
                name="name"
                className="self-stretch"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value)
                        return Promise.reject(t('Please enter product name'));
                      else return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputComponent
                  size="large"
                  placeholder={t('Enter product name')}
                />
              </Form.Item>
              <Form.Item label={t('Size')} className="self-stretch">
                <div className="grid grid-cols-3 gap-16 self-stretch">
                  <Form.Item
                    name={['sizes', 'length']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value)
                            return Promise.reject(t('Please enter length'));
                          else return Promise.resolve();
                        },
                      },
                    ]}
                    noStyle
                  >
                    <InputComponent
                      size="large"
                      placeholder={t('Length (mm)')}
                      type="number"
                    />
                  </Form.Item>
                  <Form.Item
                    name={['sizes', 'depth']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value)
                            return Promise.reject(t('Please enter depth'));
                          else return Promise.resolve();
                        },
                      },
                    ]}
                    noStyle
                  >
                    <InputComponent
                      size="large"
                      placeholder={t('Depth (mm)')}
                      type="number"
                    />
                  </Form.Item>
                  <Form.Item
                    name={['sizes', 'height']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value)
                            return Promise.reject(t('Please enter height'));
                          else return Promise.resolve();
                        },
                      },
                    ]}
                    noStyle
                  >
                    <InputComponent
                      size="large"
                      placeholder={t('Height (mm)')}
                      type="number"
                    />
                  </Form.Item>
                </div>
              </Form.Item>
              <div className="flex flex-col items-start gap-0 self-stretch">
                <Form.Item
                  label={t('Selling price')}
                  name="price_with_install"
                  className="self-stretch"
                  rules={[
                    {
                      message: t('Please enter selling price'),
                      validator: (_, value) => {
                        if (!value && canPmUpdate(user))
                          return Promise.reject(
                            t('Please enter selling price')
                          );
                        else return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputComponent
                    size="large"
                    addonBefore={<span className="text-body-medium">đ</span>}
                    placeholder={t('Enter selling price')}
                    type="number"
                  />
                </Form.Item>
                {Number(priceWithInstallWatch) > 0 && (
                  <div className="flex gap-4 items-center">
                    <span className="text-label-medium">
                      {t('Total amount')}:
                    </span>
                    <span className="text-label-medium-emphasized">
                      {convertForeignCurrency(Number(priceWithInstallWatch))}
                    </span>
                  </div>
                )}
              </div>

              <Form.Item
                label={t('Note')}
                name="description"
                className="self-stretch"
              >
                <InputComponent size="large" placeholder={t('Note')} />
              </Form.Item>
              <Form.Item
                label={t('Image gallery')}
                name="images"
                valuePropName="fileList"
                className="self-stretch"
                getValueFromEvent={e =>
                  normFileWithValidation({ e, oldFiles: imagesWatch || [] })
                }
              >
                <div className="flex flex-wrap items-center gap-16">
                  {imagesWatch?.map(item => {
                    return (
                      <div className="relative w-[140px] h-[114px]">
                        <ButtonIconGhostSmall
                          icon={<Icon.ICDelete />}
                          onClick={() => {
                            form.setFieldValue(
                              'images',
                              imagesWatch.filter(i => i.uid !== item.uid)
                            );
                          }}
                          className="absolute top-0 right-0"
                        />
                        <img
                          key={item.uid}
                          src={item.url}
                          alt=""
                          className="w-[140px] h-[114px] object-cover rounded-[4px]"
                        />
                      </div>
                    );
                  })}

                  <button
                    className="flex w-[140px] h-[114px] p-16 flex-col justify-center items-center gap-8 rounded-[4px] border-[1px] border-dashed border-outline-default-secondary bg-white"
                    type="button"
                    onClick={() => {
                      fileInputRef2.current.click();
                    }}
                  >
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>{t('Upload')}</div>
                  </button>
                  <input
                    ref={fileInputRef2}
                    type="file"
                    style={{ display: 'none' }}
                    accept="image/*"
                    multiple={true}
                  />
                </div>
              </Form.Item>
            </div>
          </Form>
        }
      />
    );
  }
};

export default ModalUpdateSpecialProduct;
